import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, ClipboardList, Mail } from "lucide-react";
import SEO from "../components/SEO";
import ResourceCard from "../components/ResourceCard";
import { ALIGNMENT_CHECK_URL, CALENDLY_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  Insights — the thinking center of AlignED.
 *  Not a blog. A curated leadership library.
 *  Every section follows the Quiet Confidence Principle™.
 * ------------------------------------------------------------------------- */

const THEMES = [
  "Leadership Clarity",
  "Consistent Execution\u2122",
  "Organizational Learning",
  "Attendance",
  "Behavior",
  "Enrollment",
  "District Leadership",
  "School Leadership",
];

const IDEAS = [
  {
    title: "Visibility Precedes Improvement\u2122",
    body: "Invisible work cannot be consistently improved.",
    href: "/insights/visibility-precedes-improvement",
    status: "published",
  },
  {
    title: "Consistency Creates Trust",
    body: "Students and staff trust systems they can depend on.",
    href: "/insights/consistency-creates-trust",
    status: "published",
  },
  {
    title: "Activity Isn\u2019t Progress",
    body: "Movement is not the same as improvement.",
    href: "/insights/activity-isnt-progress",
    status: "published",
  },
];

const GUIDES = [
  "Attendance Follow-Through Guide",
  "Behavior Consistency Guide",
  "Enrollment Follow-Up Guide",
  "Leadership Meeting Guide",
];

function ThemeChip({ label }) {
  return (
    <span
      className="inline-flex items-center px-5 py-2.5 border border-[#0A192F]/15 bg-[#FDFBF7] text-sm text-[#1A1D20] rounded-full hover:border-[#0A192F]/40 transition-colors"
      data-testid={`insights-theme-${label.replace(/\W+/g, "-").toLowerCase()}`}
    >
      {label}
    </span>
  );
}

function StayAlignedForm() {
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    // Aligned Weekly launches in July. Signup is not wired yet — capture intent visually only.
    setSubmitted(true);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl"
      data-testid="stay-aligned-form"
    >
      <label htmlFor="stay-aligned-email" className="sr-only">
        Email address
      </label>
      <div className="relative flex-1">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52796F]" strokeWidth={1.5} />
        <input
          id="stay-aligned-email"
          type="email"
          required
          placeholder="you@school.org"
          className="w-full pl-11 pr-4 py-3.5 bg-[#FDFBF7] border border-[#0A192F]/15 text-[#1A1D20] placeholder:text-[#4A5568]/60 focus:outline-none focus:border-[#0A192F]/50"
          data-testid="stay-aligned-email-input"
        />
      </div>
      <button
        type="submit"
        className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-6 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
        data-testid="stay-aligned-submit"
      >
        Subscribe to Aligned Weekly&trade;
      </button>
      {submitted && (
        <span
          className="sm:hidden text-sm text-[#52796F] leading-relaxed"
          data-testid="stay-aligned-message"
          role="status"
          aria-live="polite"
        >
          Aligned Weekly&trade; launches this July. We&apos;ll be in touch.
        </span>
      )}
      {submitted && (
        <span
          className="hidden sm:block col-span-2 text-sm text-[#52796F] leading-relaxed"
          data-testid="stay-aligned-message-desktop"
          role="status"
          aria-live="polite"
        >
          Aligned Weekly&trade; launches this July. We&apos;ll be in touch.
        </span>
      )}
    </form>
  );
}

export default function Insights() {
  return (
    <>
      <SEO
        title="Insights — Leadership ideas from the AlignED Method™ | AlignED"
        description="A curated leadership library for K–12 school and district leaders. Ideas from the AlignED Method™ on leadership, Consistent Execution™, organizational learning, and stronger student experiences."
      />

      {/* SECTION 1 — HERO */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="insights-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">
          <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">Insights</div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0A192F] max-w-4xl">
            Insights for leaders building better schools.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            Ideas from the AlignED Method&trade; on leadership, Consistent Execution&trade;, organizational learning, and stronger student experiences.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
            <a
              href="#aligned-weekly"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="insights-hero-aw-btn"
            >
              Read Aligned Weekly&trade;
            </a>
            <a
              href={ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]"
              data-testid="insights-hero-ac-btn"
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 2 — FEATURED INSIGHT */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="insights-featured">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Featured Insight</div>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-widest text-[#52796F]">
                Essay &middot; Leadership Clarity
              </div>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-[1.1]">
                Leadership Doesn&apos;t Have a Data Problem.
              </h2>
              <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
                Most schools already have data. What leaders need is clarity about what the data means and where leadership should focus next.
              </p>
              <p className="mt-6 text-sm text-[#52796F] italic" data-testid="insights-featured-status">
                Insight 001 &middot; Now published.
              </p>
              <Link
                to="/insights/leadership-doesnt-have-a-data-problem"
                className="mt-6 inline-flex items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 hover:text-[#52796F] hover:border-[#52796F] transition-colors"
                data-testid="insights-featured-cta"
              >
                Read the Insight <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-[#FDFBF7] border border-[#0A192F]/10 p-8 max-w-sm mx-auto lg:ml-auto lg:mr-0">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#52796F]">The AlignED Method&trade;</div>
                  <p className="mt-5 font-serif-display text-lg sm:text-xl text-[#0A192F] leading-snug">
                    &ldquo;Data alone does not create clarity. Attention does.&rdquo;
                  </p>
                  <div className="mt-5 w-10 h-px bg-[#38BDF8] mx-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — ALIGNED WEEKLY */}
      <section id="aligned-weekly" className="py-24 bg-[#FDFBF7]" data-testid="insights-aligned-weekly">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Newsletter</div>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#52796F] border border-[#52796F]/40 px-2.5 py-1 rounded-full">
              Coming July
            </span>
          </div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            Aligned Weekly&trade;
          </h2>
          <p className="mt-4 font-serif-display text-xl sm:text-2xl text-[#52796F] leading-snug max-w-3xl">
            Ideas that help educational leaders think more clearly and execute more consistently.
          </p>
          <p className="mt-8 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            Every school week, Aligned Weekly&trade; delivers one thoughtful leadership idea designed to help educational leaders see more clearly, ask better questions, and strengthen execution.
          </p>
          <span
            className="mt-10 inline-flex items-center gap-2 text-sm tracking-wide text-[#52796F]"
            data-testid="insights-aw-cta-disabled"
          >
            Launching Soon
          </span>
        </div>
      </section>

      {/* SECTION 4 — EXPLORE BY LEADERSHIP THEME */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="insights-themes">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Themes</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Explore by Leadership Theme
            </h2>
            <p className="mt-6 text-base text-[#4A5568] leading-relaxed">
              Choose what you care about. Insights are curated around the operational realities of school and district leadership.
            </p>
          </div>
          <div className="mt-12 flex flex-wrap gap-3">
            {THEMES.map((t) => (
              <ThemeChip key={t} label={t} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — LEADERSHIP IDEAS */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="insights-ideas">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Leadership Ideas</div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Short ideas. Long-lasting clarity.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              Short ideas from the AlignED Method&trade; that help leaders see execution, systems, and student experience more clearly.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {IDEAS.map((idea, i) => {
              const isPublished = idea.status === "published";
              const cardBody = (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest text-[#38BDF8]">Idea 0{i + 1}</div>
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${
                        isPublished
                          ? "text-[#0A192F] border border-[#D4AF37] bg-[#D4AF37]/10"
                          : "text-[#52796F] border border-[#52796F]/40"
                      }`}
                    >
                      {isPublished ? "Published" : "Coming Soon"}
                    </span>
                  </div>
                  <h3 className="mt-4 font-serif-display text-2xl text-[#0A192F] leading-snug">
                    {idea.title}
                  </h3>
                  <p className="mt-4 text-[#4A5568] leading-relaxed">{idea.body}</p>
                  {isPublished && (
                    <span className="mt-6 inline-flex items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start group-hover:gap-3 transition-all">
                      Read the Insight <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </span>
                  )}
                </>
              );

              return isPublished ? (
                <Link
                  key={idea.title}
                  to={idea.href}
                  className="group bg-[#F4F1EB]/60 border border-[#0A192F]/10 p-8 flex flex-col hover:border-[#0A192F]/40 transition-colors"
                  data-testid={`insights-idea-${i}`}
                >
                  {cardBody}
                </Link>
              ) : (
                <div
                  key={idea.title}
                  className="bg-[#F4F1EB]/60 border border-[#0A192F]/10 p-8 flex flex-col"
                  data-testid={`insights-idea-${i}`}
                >
                  {cardBody}
                </div>
              );
            })}
          </div>
          <p className="mt-10 text-sm text-[#52796F] italic">
            More leadership ideas launch alongside Aligned Weekly&trade; this July.
          </p>
        </div>
      </section>

      {/* SECTION 6 — PRACTICAL GUIDES */}
      <section className="py-24 bg-[#F4F1EB]" data-testid="insights-guides">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Practical Guides</div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#52796F] border border-[#52796F]/40 px-2.5 py-1 rounded-full">
                Coming July
              </span>
            </div>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Turning insight into consistent action.
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
              Tools and frameworks for the moments when leadership thinking meets the calendar. Guides launch alongside Aligned Weekly&trade;.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {GUIDES.map((g, i) => (
              <div
                key={g}
                className="bg-[#FDFBF7] border border-[#0A192F]/10 p-6 min-h-[140px] flex flex-col justify-between"
                data-testid={`insights-guide-${i}`}
              >
                <div className="font-serif-display text-lg text-[#0A192F] leading-snug">{g}</div>
                <div className="mt-6 text-xs uppercase tracking-widest text-[#52796F]">Coming Soon</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — ALIGNMENT CHECK CTA */}
      <section className="py-24 bg-[#FDFBF7]" data-testid="insights-ac-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#38BDF8]">The Alignment Check&trade;</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight max-w-3xl">
            See where your leadership system may be drifting.
          </h2>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed max-w-3xl">
            The Alignment Check&trade; helps leaders reflect on visibility, ownership, follow-through, and execution across their organization.
          </p>
          <div className="mt-10">
            <a
              href={ALIGNMENT_CHECK_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
              data-testid="insights-ac-cta-btn"
            >
              <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 8 — STAY ALIGNED */}
      <section className="py-24 bg-[#0A192F] text-[#FDFBF7]" data-testid="insights-stay-aligned">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Stay AlignED</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Receive one thoughtful leadership idea each school week.
          </h2>
          <p className="mt-6 text-lg text-[#FDFBF7]/85 leading-relaxed max-w-3xl">
            No hype. No clutter. Just practical thinking for educational leaders working to create more consistent execution.
          </p>
          <StayAlignedForm />
          <p className="mt-4 text-sm text-[#FDFBF7]/65 leading-relaxed max-w-2xl">
            Aligned Weekly&trade; launches this July.
          </p>
          <div className="mt-14 pt-8 border-t border-[#FDFBF7]/15 flex flex-wrap gap-6 text-sm">
            <Link
              to="/alignment-check"
              className="text-[#FDFBF7]/85 hover:text-[#D4AF37] transition-colors border-b border-[#FDFBF7]/30 pb-0.5"
              data-testid="insights-footer-ac-link"
            >
              Take the Alignment Check&trade;
            </Link>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="text-[#FDFBF7]/85 hover:text-[#D4AF37] transition-colors border-b border-[#FDFBF7]/30 pb-0.5"
              data-testid="insights-footer-walkthrough-link"
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />
              Schedule a Walkthrough
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
